import clsx from "clsx";
import { useAtomValue } from "jotai";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type React from "react";
import { currentTheme } from "../../../../atoms";
import type { Side, SmetaSplitterBarProps } from "./SmetaSplitterConsts";
import styles from "./splitter.module.css";

const collapseSideClassMap = {
  left: styles.expandLeft,
  right: styles.expandRight,
  top: styles.expandTop,
  bottom: styles.expandBottom,
} satisfies Record<Side, string>;

const doubleClickInterval = 400;

// eslint-disable-next-line @typescript-eslint/naming-convention
const SmetaSplitterBar = (props: SmetaSplitterBarProps) => {
  const theme = useAtomValue(currentTheme);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const separatorRef = useRef<HTMLDivElement>(null!);
  const hoverTimeoutRef = useRef<NodeJS.Timeout>(undefined);
  const isHoveredRef = useRef(false);
  const lastPointerDownTimeRef = useRef(-Infinity);

  // Размеры и состояние сплиттера меняются императивно, в том числе через его ref.
  useLayoutEffect(() => {
    const separator = separatorRef.current;
    const updateCollapsed = () => setIsCollapsed(separator.hasAttribute("data-itemcollapsed"));
    updateCollapsed();
    const observer = new MutationObserver(updateCollapsed);
    observer.observe(separator, { attributes: true, attributeFilter: ["data-itemcollapsed"] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => () => clearTimeout(hoverTimeoutRef.current), []);

  useEffect(() => {
    if (props.blockResize) {
      separatorRef.current.setAttribute("block-resize", "");
    } else separatorRef.current.removeAttribute("block-resize");
  }, [props.blockResize]);

  const handleMouseEnter: React.MouseEventHandler<HTMLDivElement> = (ev) => {
    const target = ev.currentTarget;
    isHoveredRef.current = true;
    clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(() => {
      if (isHoveredRef.current) {
        target.setAttribute("data-active", "");
      }
    }, 200);
  };

  const handleMouseLeave: React.MouseEventHandler<HTMLDivElement> = (ev) => {
    const target = ev.currentTarget;
    isHoveredRef.current = false;
    clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = undefined;
    target.removeAttribute("data-active");
  };

  // dblclick до полосы не доходит: на pointerdown сплиттер начинает перетаскивание и перекрывает экран
  // оверлеем, поэтому mouseup попадает уже на него. Двойное нажатие распознаём по двум pointerdown подряд.
  const handlePointerDown: React.PointerEventHandler<HTMLDivElement> = (ev) => {
    if (props.hideButtons || ev.target !== ev.currentTarget || ev.button !== 0) return;
    if (ev.timeStamp - lastPointerDownTimeRef.current > doubleClickInterval) {
      lastPointerDownTimeRef.current = ev.timeStamp;
      return;
    }
    lastPointerDownTimeRef.current = -Infinity;
    // Не даём сплиттеру начать перетаскивание вторым нажатием.
    ev.stopPropagation();
    props.onToggle();
  };

  const label = isCollapsed ? "Распахнуть панель" : "Запахнуть панель";
  const pointsToStart = (props.primaryIndex === "end") !== isCollapsed;

  return (
    <div
      role="presentation"
      ref={separatorRef}
      hidden={props.hideSplitter}
      className={clsx(
        styles.splitterBar,
        { [styles.light]: theme === "light" },
        collapseSideClassMap[props.collapseSide],
      )}
      data-primaryelement={props.primaryIndex}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onPointerDown={handlePointerDown}
    >
      {!props.hideButtons && (
        <button
          type="button"
          className={styles.collapseButton}
          title={`${label} (двойной клик по разделителю)`}
          aria-label={label}
          aria-expanded={!isCollapsed}
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation();
            props.onToggle();
          }}
        >
          <span className={styles.collapseButtonHitArea} aria-hidden="true" />
          <svg width="10" height="10" viewBox="0 0 12 12" fill="none" aria-hidden="true">
            <path
              d={
                props.orientation === "vertical"
                  ? pointsToStart
                    ? "M8 2 4 6 8 10"
                    : "M4 2 8 6 4 10"
                  : pointsToStart
                    ? "M2 8 6 4 10 8"
                    : "M2 4 6 8 10 4"
              }
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}
    </div>
  );
};
export default SmetaSplitterBar;
